// Dark header strip at the top of the BMD report dashboard. Follows the
// layout in `_local/DA.pdf`: Student, Enrolled, BMD Base Level (dropdown) and
// Session columns, with a status pill on the right.
//
// The session column shows the most recent attempt date for the selected year
// level. It becomes a session selector once the per-session scope toggle
// lands.

import React from "react"
import type {
  BmdReportStudent,
  BmdReportYearLevel,
  BmdReportSession,
} from "../types/bmd-report.types"

const DROPDOWN_CHIP_STYLE: React.CSSProperties = {
  backgroundColor: "rgba(255,255,255,0.08)",
  border: "1px solid rgba(255,255,255,0.18)",
  cursor: "pointer",
}

const DROPDOWN_SELECT_STYLE: React.CSSProperties = {
  backgroundColor: "transparent",
  color: "white",
  boxShadow: "none",
  backgroundImage: "none",
  appearance: "none",
  WebkitAppearance: "none",
  MozAppearance: "none",
  cursor: "pointer",
}

const SURFACE = "#1f2a44"

type ColumnProps = {
  label: string
  children: React.ReactNode
}

const Column: React.FC<ColumnProps> = ({ label, children }) => (
  <div className="me-4">
    <div
      className="text-uppercase font-size-11 fw-semibold mb-1"
      style={{ color: "rgba(255,255,255,0.55)", letterSpacing: 0.6 }}
    >
      {label}
    </div>
    <div className="fw-bold font-size-15 text-white">{children}</div>
  </div>
)

type StatusVariant = "success" | "secondary" | "warning" | "info"


// "6 Aug 2026 · Completed". The date is when the student started answering,
// which is what a tutor recognises as the session.
function formatSessionLabel(sess: BmdReportSession): string {
  const when = sess.attempted_on
    ? new Date(sess.attempted_on).toLocaleDateString(undefined, {
        year: "numeric",
        month: "short",
        day: "numeric",
      })
    : "Undated"
  const status = (sess.flow_status || "").replace(/_/g, " ").toLowerCase()
  return status ? `${when} · ${status}` : when
}

/** One choice in the subject switcher. `key` is whatever the page routes on. */
export type BmdReportSubject = {
  key: string
  label: string
}

export type BmdDashboardHeaderProps = {
  student: BmdReportStudent
  // Tutor's full student list for the in-header switcher. Empty array
  // collapses the column back to a static name.
  students: BmdReportStudent[]
  onStudentChange: (studentKey: string) => void
  availableYearLevels: BmdReportYearLevel[]
  selectedYearLevel: BmdReportYearLevel | null
  onYearLevelChange: (yearLevelId: number) => void
  sessionDate: string | null
  // Report scope. An empty `sessions`, or no onSessionChange, collapses the
  // column back to the static date it showed before. That is how this feature
  // gets removed.
  sessions?: BmdReportSession[]
  selectedSessionKey?: string | null
  onSessionChange?: (key: string | null) => void
  /**
   * Subject switcher. Fewer than two choices, or no handler, and the column is not drawn at
   * all - a dropdown with one option is just a label that looks clickable.
   */
  subjects?: BmdReportSubject[]
  selectedSubject?: string | null
  onSubjectChange?: (key: string) => void
  /** "BMD Base Level" for maths. English says "English Base Level"; BMD is a maths name. */
  baseLevelLabel?: string
  statusLabel: string
  statusVariant: StatusVariant
}

export const BmdDashboardHeader: React.FC<BmdDashboardHeaderProps> = ({
  student,
  students,
  onStudentChange,
  availableYearLevels,
  selectedYearLevel,
  onYearLevelChange,
  sessionDate,
  sessions = [],
  selectedSessionKey = null,
  onSessionChange,
  subjects = [],
  selectedSubject = null,
  onSubjectChange,
  baseLevelLabel = "BMD Base Level",
  statusLabel,
  statusVariant,
}) => {
  // If the in-flight report has a student that isn't in the tutor's list
  // (cache mismatch / new enrolment), still render it as an option so the
  // current selection doesn't appear blank.
  const studentInList = students.some(
    (s) => s.pk_student_key === student.pk_student_key,
  )
  const renderStudents =
    studentInList || students.length === 0
      ? students
      : [student, ...students]

  return (
    <div
      className="rounded-top px-4 py-3 d-flex flex-wrap align-items-center"
      style={{ backgroundColor: SURFACE }}
    >
      <Column label="Student">
        {students.length === 0 ? (
          student.name
        ) : (
          <div
            className="d-inline-flex align-items-center px-2 py-1 rounded"
            style={DROPDOWN_CHIP_STYLE}
          >
            <select
              className="form-select form-select-sm fw-bold border-0 p-0 pe-1"
              style={{ ...DROPDOWN_SELECT_STYLE, minWidth: 150 }}
              value={student.pk_student_key}
              onChange={(e) => onStudentChange(e.target.value)}
              aria-label="Switch student"
            >
              {renderStudents.map((s) => (
                <option
                  key={s.pk_student_key}
                  value={s.pk_student_key}
                  style={{ color: "black" }}
                >
                  {s.name}
                </option>
              ))}
            </select>
            <i
              className="fas fa-chevron-down ms-1 font-size-11"
              style={{ color: "rgba(255,255,255,0.7)" }}
              aria-hidden="true"
            ></i>
          </div>
        )}
      </Column>
      {/* Which subject's diagnostics are on screen. Collapses entirely when the page does not
          offer a choice, the same way the Session column does - that is how this gets removed. */}
      {subjects.length > 1 && onSubjectChange ? (
        <Column label="Subject">
          <div
            className="d-inline-flex align-items-center px-2 py-1 rounded"
            style={DROPDOWN_CHIP_STYLE}
          >
            <select
              className="form-select form-select-sm fw-bold border-0 p-0 pe-1"
              style={{ ...DROPDOWN_SELECT_STYLE, minWidth: 110 }}
              value={selectedSubject || subjects[0].key}
              onChange={(e) => onSubjectChange(e.target.value)}
              aria-label="Switch subject"
            >
              {subjects.map((s) => (
                <option key={s.key} value={s.key} style={{ color: "black" }}>
                  {s.label}
                </option>
              ))}
            </select>
            <i
              className="fas fa-chevron-down ms-1 font-size-11"
              style={{ color: "rgba(255,255,255,0.7)" }}
              aria-hidden="true"
            ></i>
          </div>
        </Column>
      ) : null}
      <Column label="Enrolled">
        {student.enrolment_year_level_name || "—"}
      </Column>
      <Column label={baseLevelLabel}>
        {availableYearLevels.length === 0 ? (
          <span>No data yet</span>
        ) : (
          <div
            className="d-inline-flex align-items-center px-2 py-1 rounded"
            style={{
              backgroundColor: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
              cursor: "pointer",
            }}
          >
            <select
              className="form-select form-select-sm fw-bold border-0 p-0 pe-1"
              style={{
                backgroundColor: "transparent",
                color: "white",
                minWidth: 110,
                boxShadow: "none",
                backgroundImage: "none",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                cursor: "pointer",
              }}
              value={selectedYearLevel?.year_level_id ?? ""}
              onChange={(e) => onYearLevelChange(Number(e.target.value))}
            >
              {availableYearLevels.map((y) => (
                <option
                  key={y.year_level_id}
                  value={y.year_level_id}
                  style={{ color: "black" }}
                >
                  {y.level_name}
                </option>
              ))}
            </select>
            <i
              className="fas fa-chevron-down ms-1 font-size-11"
              style={{ color: "rgba(255,255,255,0.7)" }}
              aria-hidden="true"
            ></i>
          </div>
        )}
      </Column>
      <Column label="Session">
        {onSessionChange && sessions.length > 0 ? (
          <div
            className="d-inline-flex align-items-center px-2 py-1 rounded"
            style={{
              backgroundColor: "rgba(255,255,255,0.08)",
              border: "1px solid rgba(255,255,255,0.18)",
              cursor: "pointer",
            }}
          >
            <select
              className="form-select form-select-sm fw-bold border-0 p-0 pe-1"
              style={{
                backgroundColor: "transparent",
                color: "white",
                minWidth: 150,
                boxShadow: "none",
                backgroundImage: "none",
                appearance: "none",
                WebkitAppearance: "none",
                MozAppearance: "none",
                cursor: "pointer",
              }}
              value={selectedSessionKey ?? ""}
              onChange={(e) => onSessionChange(e.target.value || null)}
            >
              <option value="" style={{ color: "black" }}>
                All sessions
              </option>
              {sessions.map((sess) => (
                <option
                  key={sess.diagnostics_session_key}
                  value={sess.diagnostics_session_key}
                  style={{ color: "black" }}
                >
                  {formatSessionLabel(sess)}
                </option>
              ))}
            </select>
            <i
              className="fas fa-chevron-down ms-1 font-size-11"
              style={{ color: "rgba(255,255,255,0.7)" }}
              aria-hidden="true"
            ></i>
          </div>
        ) : (
          sessionDate || "—"
        )}
      </Column>

      <div className="ms-auto">
        <span
          className={`badge bg-${statusVariant} bg-opacity-25 text-${statusVariant} font-size-12 px-3 py-2`}
          style={{ border: `1px solid var(--bs-${statusVariant})` }}
        >
          {statusLabel}
        </span>
      </div>
    </div>
  )
}

export default BmdDashboardHeader
